import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";
import { Reveal, ChapterHeading } from "./Reveal";

const COACH_IMG = "/photos/tammy.jpg";

const CoachSection = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <section id="coach" ref={ref} className="relative py-24 sm:py-32 bg-sage-dark grain overflow-hidden" data-testid="coach-section">
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-20 w-[30rem] h-[30rem] rounded-full bg-sage/15 blur-3xl"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5 relative">
          <Reveal>
            <div className="absolute -bottom-6 -left-6 w-full h-full rounded-3xl border-2 border-brand/50" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-3xl shadow-[0_20px_50px_rgb(0,0,0,0.25)]">
              <motion.img
                src={COACH_IMG}
                alt="Tammy Porter, coach and owner of Me 1st Fitness & Coaching in Red Deer"
                style={{ y: imgY }}
                className="w-full h-[28rem] sm:h-[34rem] object-cover object-[center_20%] scale-110"
                data-testid="coach-image"
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <ChapterHeading number="07" label="Your Coach" title="Meet Your Coach" dark />
          <Reveal delay={0.1}>
            <p className="mt-4 font-display text-xl font-semibold text-sage" data-testid="coach-name">
              Tammy Porter
              <span className="ml-3 align-middle text-[10px] uppercase tracking-[0.2em] font-bold text-brand bg-brand/15 rounded-full px-3 py-1">
                Group Fitness Instructor · Fitness &amp; Nutrition Coach
              </span>
            </p>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-sand/70 max-w-2xl">
              "My passion for fitness started when I recognized the connection between my physical
              health and mental wellness. As time progressed I began learning how nutrition,
              movement, sleep and natural remedies were changing my life in the most positive ways.
              I am just a mom, with a passion to teach others what I have learned along the way."
            </p>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-sand/70 max-w-2xl">
              That passion became Me 1st Fitness &amp; Coaching — a welcoming, judgment-free studio
              in Red Deer where Tammy helps everyday people build strength, confidence and habits
              that actually fit real life. Her philosophy is simple: meet people where they are,
              celebrate every win, and build progress that lasts.
            </p>
            <p className="mt-4 text-sm text-sand/50 max-w-2xl">
              Tammy is AFLCA-certified (Fitness Alberta) in Group Conditioning, Group Choreography
              and Older Adult fitness, and Bone Fit™ trained through Osteoporosis Canada.
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-8 flex items-start gap-4 max-w-xl">
              <Quote size={28} className="text-brand shrink-0 mt-1" />
              <p className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-sand leading-snug">
                "Let's make your health a priority."
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <button
              onClick={() => navigate("/contact")}
              data-testid="coach-work-with-me-btn"
              className="group mt-10 rounded-full bg-brand text-charcoal font-bold px-8 py-4 text-sm tracking-wide inline-flex items-center gap-2 hover:bg-sand transition-colors duration-300"
            >
              WORK WITH ME
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default CoachSection;
